import React, { useMemo } from 'react';
import { CollapsibleDetailsSection } from '../../components';
import { formatCurrency, formatDate } from '@shared/utils/formatters';
import type { Product } from '@shared/types/products';
import type { PaymentDetail } from '../../types/panel-common.types';

interface ProductProfileSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  isDetailView?: boolean;
  onEdit?: () => void;
  product?: Product;
}

export const ProductProfileSection = React.memo<ProductProfileSectionProps>(({
  isExpanded,
  onToggle,
  isDetailView = false,
  onEdit,
  product
}) => {
  const profileDetails: PaymentDetail[] = useMemo(() => {
    if (!product) return [];

    const details: PaymentDetail[] = [
      { label: 'ID', value: product.id, isCopyable: true },
      { label: 'Name', value: product.name },
      { label: 'Type', value: product.type.charAt(0).toUpperCase() + product.type.slice(1) },
      { label: 'Category', value: product.category || '—' },
    ];

    if (product.sku) {
      details.push({ label: 'SKU', value: product.sku, isCopyable: true });
    }

    details.push({
      label: 'Price',
      value: formatCurrency(product.price, product.currency, 2),
    });

    details.push({ label: 'Currency', value: product.currency });

    if (product.stock !== undefined) {
      details.push({ label: 'Stock', value: `${product.stock} units` });
    }

    details.push({ label: 'Created', value: formatDate(product.created) });
    details.push({ label: 'Last Updated', value: formatDate(product.lastUpdated) });

    return details;
  }, [product]);

  return (
    <CollapsibleDetailsSection
      title="Product"
      details={profileDetails}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isDetailView={isDetailView}
      emptyMessage="No product details available"
      actions={onEdit ? [{ label: 'Edit', onClick: onEdit }] : undefined}
    />
  );
});

ProductProfileSection.displayName = 'ProductProfileSection';
